import React, { useState, useRef, useEffect } from 'react';
import { galleryImages } from '../mockData';

// Gallery caption templates based on Priority 6
const captionTemplates = [
  { title: 'Metal roof replacement', suburb: 'Cecil Hills', description: 'Removed the ageing roof and completed a clean new metal roof installation to improve durability and street appeal.' },
  { title: 'Roof repair', suburb: 'Liverpool', description: 'Carried out targeted repairs to address leaks and restore reliable protection for the home.' },
  { title: 'Gutter replacement', suburb: 'Fairfield', description: 'Upgraded old gutters and drainage components to improve water flow and reduce overflow risk.' },
  { title: 'Roof and gutter upgrade', suburb: 'Cabramatta', description: 'Completed a practical exterior upgrade with improved roofing performance and a neater finish.' },
  { title: 'Metal roof replacement', suburb: 'Wetherill Park', description: 'Removed the ageing roof and completed a clean new metal roof installation to improve durability and street appeal.' },
  { title: 'Roof repair', suburb: 'Greenacre', description: 'Carried out targeted repairs to address leaks and restore reliable protection for the home.' },
  { title: 'Gutter replacement', suburb: 'Bankstown', description: 'Upgraded old gutters and drainage components to improve water flow and reduce overflow risk.' },
  { title: 'Roof and gutter upgrade', suburb: 'Parramatta', description: 'Completed a practical exterior upgrade with improved roofing performance and a neater finish.' },
  { title: 'Metal roof replacement', suburb: 'Penrith', description: 'Removed the ageing roof and completed a clean new metal roof installation to improve durability and street appeal.' },
  { title: 'Roof repair', suburb: 'Blacktown', description: 'Carried out targeted repairs to address leaks and restore reliable protection for the home.' },
  { title: 'Gutter replacement', suburb: 'Mount Druitt', description: 'Upgraded old gutters and drainage components to improve water flow and reduce overflow risk.' },
  { title: 'Roof and gutter upgrade', suburb: 'Campbelltown', description: 'Completed a practical exterior upgrade with improved roofing performance and a neater finish.' },
  { title: 'Metal roof replacement', suburb: 'Sutherland', description: 'Removed the ageing roof and completed a clean new metal roof installation to improve durability and street appeal.' },
  { title: 'Roof repair', suburb: 'Hurstville', description: 'Carried out targeted repairs to address leaks and restore reliable protection for the home.' },
  { title: 'Gutter replacement', suburb: 'Kogarah', description: 'Upgraded old gutters and drainage components to improve water flow and reduce overflow risk.' },
];

// Lazy loading image component
const LazyImage = ({ src, alt, className }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const imgRef = useRef();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: '100px' }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div ref={imgRef} className="w-full h-full">
      {isInView ? (
        <img
          src={src}
          alt={alt}
          className={`${className} ${isLoaded ? 'opacity-100' : 'opacity-0'} transition-opacity duration-300`}
          onLoad={() => setIsLoaded(true)}
          loading="lazy"
        />
      ) : (
        <div className="w-full h-full bg-gray-200 animate-pulse" />
      )}
      {isInView && !isLoaded && (
        <div className="absolute inset-0 bg-gray-200 animate-pulse" />
      )}
    </div>
  );
};

export const GallerySection = () => {
  return (
    <section id="gallery" className="section-padding bg-gray-50" data-testid="gallery-section">
      <div className="container mx-auto px-4">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Our <span className="text-primary">Recent Projects</span>
          </h2>
          <p className="text-lg text-gray-600">
            See the quality of our workmanship across Greater Sydney
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {galleryImages.map((image, index) => {
            const caption = captionTemplates[index % captionTemplates.length];
            // Use image URL hash for unique key
            const imageKey = `gallery-${index}-${image.slice(-15)}`;
            return (
              <div
                key={imageKey}
                className="group relative overflow-hidden rounded-xl shadow-lg cursor-pointer aspect-[4/3]"
                data-testid={`gallery-item-${index}`}
              >
                <LazyImage
                  src={image}
                  alt={`${caption.title} - ${caption.suburb}`}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                    <h3 className="text-lg font-bold mb-1">{caption.title} - {caption.suburb}</h3>
                    <p className="text-sm text-gray-300 line-clamp-2">{caption.description}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
